import React from 'react';
import { Platform } from 'react-native';
import { config } from '../config';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
// Web Stripe SDK (real on web via webElements.web.tsx, null stubs on
// native via webElements.tsx — Vite/Metro pick the right file via
// extension priority).  No more require()/try-catch dance — Vite has
// no `require` in the browser bundle and the silent catch was
// breaking the Add Card flow.
import { Elements as webElements, loadStripe as webLoadStripe } from './webElements';
// Native Stripe SDK (real on native via nativeStripe.tsx, null stubs
// on web via nativeStripe.web.tsx — same pattern).
import { NativeStripeProvider } from './nativeStripe';

/** Get the Stripe publishable key — dynamic from auth context, fallback to env config */
function useStripeKey(): string {
  try {
    const { stripePublishableKey } = useAuth();
    return stripePublishableKey || config.stripePublishableKey;
  } catch {
    // useAuth not available (outside provider) — use static config
    return config.stripePublishableKey;
  }
}

// Cache Stripe.js promises per key to avoid re-initialization
const stripePromiseCache = new Map<string, any>();

/** getStripePromise - lazily loads and caches the Stripe.js instance (web only). */
export function getStripePromise(publishableKey: string, connectedAccountId?: string) {
  if (Platform.OS !== 'web' || !webLoadStripe || !publishableKey) return null;
  if (connectedAccountId) {
    // Connected account instances are not cached (unique per account)
    return webLoadStripe(publishableKey, { stripeAccount: connectedAccountId });
  }
  const cacheKey = publishableKey;
  if (!stripePromiseCache.has(cacheKey)) {
    stripePromiseCache.set(cacheKey, webLoadStripe(publishableKey));
  }
  return stripePromiseCache.get(cacheKey);
}

interface StripePaymentProviderProps {
  clientSecret: string;
  connectedAccountId?: string;
  children: React.ReactNode;
}

/** StripePaymentProvider - wraps children with Stripe Elements (web) or native StripeProvider. */
export function StripePaymentProvider({ clientSecret, connectedAccountId, children }: StripePaymentProviderProps) {
  const { colors, isDark } = useTheme();
  const publishableKey = useStripeKey();

  if (Platform.OS === 'web') {
    if (!webElements || !publishableKey) return <>{children}</>;
    const Elements = webElements;
    const stripe = getStripePromise(publishableKey, connectedAccountId);
    return (
      <Elements
        stripe={stripe}
        options={{
          clientSecret,
          appearance: {
            theme: isDark ? 'night' : 'stripe',
            variables: { colorPrimary: colors.primary, borderRadius: '8px' },
          },
        }}
      >
        {children}
      </Elements>
    );
  }

  // Native
  if (NativeStripeProvider && publishableKey) {
    return (
      <NativeStripeProvider
        publishableKey={publishableKey}
        stripeAccountId={connectedAccountId}
      >
        {children}
      </NativeStripeProvider>
    );
  }

  // Fallback if native Stripe SDK not available
  return <>{children}</>;
}

/**
 * Root-level Stripe provider for the native app.
 * Wrap your app root with this on native platforms.
 */
export function StripeRootProvider({ children }: { children: React.ReactNode }) {
  if (Platform.OS === 'web') return <>{children}</>;
  if (NativeStripeProvider) {
    const key = useStripeKey();
    if (!key) return <>{children}</>;
    return (
      <NativeStripeProvider
        publishableKey={key}
        merchantIdentifier="merchant.app.rinsepay"
      >
        {children}
      </NativeStripeProvider>
    );
  }
  return <>{children}</>;
}
